import React, { useEffect, useState } from "react";
import api from "API/api";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import { Info } from "@mui/icons-material";
import DatePicker from "react-datepicker";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import colors from "assets/theme/base/colors";
import useAnalyticsStore from "store/analyticsStore";
import { S3SignedUrl } from "../../components/Common/s3Bucket";
import CircularProgress from "@mui/material/CircularProgress";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import VideoPreviewModal from "../../components/Modals/VideoPreviewModal/VideoPreviewModal";
import {
  Card,
  Grid,
  Box,
  Select,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Button,
  Icon,
  Stack,
  IconButton,
  Typography,
  Paper,
} from "@mui/material";
import NoPie from "../../assets/images/no-pie.png";
import NoBar from "../../assets/images/no-bar.png";
import Shimmer from "../../assets/gif/shimmer.gif";
import { ImageCell } from "../../components/Common/imageCell";
import { Pagination } from "../../components/Common/pagination";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function groupRecordsIntoIntervals(records, rangeType, startDate, endDate) {
  if (!records.length) return { categories: [], series: [] };

  let intervalHours = 4;
  let totalIntervals = 6;
  let labelFormat = "HH:mm";
  let minTime, maxTime;

  const now = dayjs();

  if (rangeType === "24hours") {
    intervalHours = 4;
    totalIntervals = 6;
    maxTime = now;
    minTime = now.subtract(24, "hour");
  } else if (rangeType === "48hours") {
    intervalHours = 8;
    totalIntervals = 6;
    maxTime = now;
    minTime = now.subtract(48, "hour");
  } else if (rangeType === "72hours") {
    intervalHours = 12;
    totalIntervals = 6;
    maxTime = now;
    minTime = now.subtract(72, "hour");
  } else if (rangeType === "week") {
    intervalHours = 24;
    totalIntervals = 7;
    maxTime = now.endOf("day");
    minTime = now.subtract(6, "day").startOf("day");
    labelFormat = "ddd";
  } else if (rangeType === "custom" && startDate && endDate) {
    minTime = dayjs(startDate);
    maxTime = dayjs(endDate);
    const diffHours = maxTime.diff(minTime, "hour");
    intervalHours = Math.ceil(diffHours / 6);
    totalIntervals = 6;
    labelFormat = "HH:mm";
  } else if (rangeType === "all") {
    records.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    minTime = dayjs(records[0].created_at);
    maxTime = dayjs(records[records.length - 1].created_at);
    const days = maxTime.diff(minTime, "day") + 1;
    totalIntervals = days;
    intervalHours = 24;
    labelFormat = "YYYY-MM-DD";
  } else {
    records.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    minTime = dayjs(records[0].created_at);
    maxTime = dayjs(records[records.length - 1].created_at);
    intervalHours = maxTime.diff(minTime, "hour") || 1;
    totalIntervals = 1;
  }

  let intervals = [];
  for (let i = 0; i < totalIntervals; i++) {
    const start = minTime.add(i * intervalHours, "hour");
    const end = start.add(intervalHours, "hour");
    intervals.push({ start, end });
  }

  let counts = Array(totalIntervals).fill(0);
  records.forEach((rec) => {
    const recTime = dayjs(rec.created_at);
    try {
      for (let i = 0; i < intervals.length; i++) {
        if (
          recTime.isSameOrAfter(intervals[i].start) &&
          recTime.isBefore(intervals[i].end)
        ) {
          counts[i]++;
          break;
        }
      }
    } catch (error) {
      console.log(error);
    }
  });

  const categories = intervals.map((intv) => {
    if (rangeType === "week") {
      return intv.start.format(labelFormat);
    }
    return `${intv.start.format("HH:mm")}-${intv.end.format("HH:mm")}`;
  });

  return { categories, series: counts };
}

const Analytics = () => {
  const navigate = useNavigate();
  const { white, gradients } = colors;
  const [siteData, setSiteData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadTimer, setDownloadTimer] = useState(null);
  const [violationsData, setViolationsData] = useState([]);
  const [violationTypes, setViolationTypes] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [localDateRange, setLocalDateRange] = useState([null, null]);
  const [barChartData, setBarChartData] = useState({
    categories: [],
    series: [{ name: "Violations", data: [] }],
  });
  const [pieChartData, setPieChartData] = useState({ labels: [], series: [] });
  const [selectedService, setSelectedService] = useState("all");
  const {
    selectedSiteId = "",
    selectedOption = "all",
    selectedViolation = "",
    paginationUrls = { next: null, prev: null },
    selectedDevice = "all",
    setSelectedDevice,
    setDateRange,
    setDownloadingUrl,
    setSelectedSiteId,
    setSelectedOption,
    setPaginationUrls,
    setSelectedLocation,
    setSelectedViolation,
  } = useAnalyticsStore() || {};

  const BUCKET = "benchmark-violation-videos";
  const selectedLocation =
    siteData?.find((site) => site?.site_id === selectedSiteId) || null;

  const startDate = localDateRange[0] ? new Date(localDateRange[0]) : null;
  const endDate = localDateRange[1] ? new Date(localDateRange[1]) : null;

  const formatDate = (date) => {
    if (!date) return null;
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      const offset = d.getTimezoneOffset();
      d.setMinutes(d.getMinutes() - offset);
      return d.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  const handleDateRangeChange = (update) => {
    if (Array.isArray(update)) {
      setLocalDateRange(update);
      if (update[0] && update[1]) {
        setDateRange(update);
      }
    }
  };

  const handleSelectChange = (e) => {
    const newValue = e.target.value;
    setSelectedOption(newValue);
    if (newValue === "custom") {
      setLocalDateRange([null, null]);
    } else {
      const today = new Date();
      setLocalDateRange([today, today]);
    }
  };

  const toUrlFriendly = (text) => {
    if (!text || typeof text !== "string") return "";
    return text.toLowerCase().replace(/\s+/g, "-");
  };

  const onBarClick = (index) => {
    let violationType;
    if (selectedService === "Intrusion Detection") {
      violationType = "intrusion";
    } else {
      violationType = barChartData.categories[index];
    }
    const urlFriendlyViolationType = toUrlFriendly(violationType);

    navigate(`/analysis-data/${urlFriendlyViolationType}`, {
      state: {
        violationType,
        selectedLocation,
      },
    });
  };

  const exportToExcel = async () => {
    setIsLoading(true);
    try {
      let apiUrl = `/violations?site_id=${selectedLocation.site_id}&no_pagination=true`;

      if (selectedOption !== "all") {
        if (selectedOption === "custom" && startDate && endDate) {
          const formattedStart = formatDate(startDate);
          const formattedEnd = formatDate(endDate);
          if (formattedStart && formattedEnd) {
            apiUrl += `&start_date=${formattedStart}&end_date=${formattedEnd}`;
          } else {
            console.log("Invalid date range, skipping date filter");
          }
        } else if (selectedOption === "all") {
          apiUrl += `&time_range=all`;
        } else {
          apiUrl += `&time_range=${selectedOption}`;
        }
      }

      if (selectedViolation) {
        apiUrl += `&violation_type=${selectedViolation}`;
      }

      const res = await api.get(apiUrl);
      const fullData = res?.data?.data || [];

      if (!fullData || fullData.length === 0) {
        toast.warning("No data found for export.");
        return;
      }

      const excelRows = [];

      fullData.forEach((item) => {
        item.violations.forEach(({ violation, records }) => {
          records.forEach((record) => {
            excelRows.push({
              "Violation Type": violation,
              Area: record.area,
              Timestamp: formatDate(record.created_at),
              Description: record.description,
            });
          });
        });
      });

      const ws = XLSX.utils.json_to_sheet(excelRows);

      const headerCells = ["A1", "B1", "C1", "D1"];
      headerCells.forEach((cell) => {
        if (!ws[cell]) return;
        ws[cell].s = {
          fill: { patternType: "solid", fgColor: { rgb: "063863" } },
          font: { bold: true, color: { rgb: "FFFFFF" } },
          alignment: { horizontal: "center", vertical: "center" },
        };
      });

      const wb = {
        Sheets: { Violations: ws },
        SheetNames: ["Violations"],
      };

      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      });

      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      FileSaver.saveAs(data, "violations.xlsx");
    } catch (error) {
      toast.error("Failed to export violations");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const barChartOptions = {
    chart: {
      type: "bar",
      height: 350,
      stacked: false,
      events: {
        click: function (event, chartContext, { dataPointIndex }) {
          if (typeof dataPointIndex === "number" && dataPointIndex !== -1)
            onBarClick(dataPointIndex);
        },
      },
      toolbar: {
        show: true,
        tools: {
          download: false,
        },
      },
    },
    colors: barChartData.categories.map((_, index) => {
      const colors = [
        "#2E86AB",
        "#F6C85F",
        "#6FB07F",
        "#9B6A6C",
        "#BC5090",
        "#58508D",
        "#FFA600",
        "#003F5C",
        "#7A5195",
        "#EF5675",
      ];
      return colors[index % colors.length];
    }),
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 3,
        columnWidth: "25%",
        distributed: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: barChartData.categories,
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val + " violations";
        },
      },
    },
    legend: {
      show: false,
    },
  };

  const handleLocationChange = (e) => {
    setSelectedSiteId(e.target.value);
    setSelectedDevice("all");
    setSelectedService("all");
  };

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get("/cv/siteapi");
        const data = response?.data?.data || [];
        setSiteData(data);

        if (data.length > 0 && !selectedLocation) {
          setSelectedLocation(data[0].site_id);
        }
      } catch (error) {
        console.error("Fetch sites error:", error);
        setSiteData([]);
        toast.error(error?.response?.data?.message || "Failed to fetch sites");
      }
    };

    fetchSites();
  }, []);

  const buildApiUrl = () => {
    let apiUrl = `/violations?`;

    if (selectedLocation?.site_id) {
      apiUrl += `site_id=${selectedLocation.site_id}`;
    }
    if (selectedDevice && selectedDevice !== "all") {
      apiUrl += `&device_id=${selectedDevice}`;
    }
    if (selectedService && selectedService !== "all") {
      apiUrl += `&service_type=${encodeURIComponent(selectedService)}`;
    }
    if (selectedViolation && selectedViolation !== "all") {
      apiUrl += `&violation_type=${encodeURIComponent(selectedViolation)}`;
    }
    if (selectedOption && selectedOption !== "all") {
      if (selectedOption === "custom" && startDate && endDate) {
        const formattedStart = formatDate(startDate);
        const formattedEnd = formatDate(endDate);
        if (formattedStart && formattedEnd) {
          apiUrl += `&start_date=${formattedStart}&end_date=${formattedEnd}`;
        }
      } else if (selectedOption === "all") {
        apiUrl += `&time_range=all`;
      } else {
        apiUrl += `&time_range=${selectedOption}`;
      }
    }
    return apiUrl;
  };

  const fetchDatawithPagination = async (url = null) => {
    setViolationsData([]);
    if (!selectedLocation?.site_id) {
      setViolationTypes([]);
      setPieChartData({ labels: [], series: [] });
      setBarChartData({
        categories: [],
        series: [{ name: "Violations", data: [] }],
      });
      setPaginationUrls({ next: null, prev: null });
      setLoading(false);
      return;
    }
    if (
      selectedOption === "custom" &&
      (!localDateRange[0] || !localDateRange[1])
    ) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      let apiUrl = url || buildApiUrl();

      const res = await api.get(apiUrl);
      const results = res?.data?.results || {};
      const dataList = results?.data || [];
      const nextUrl = res?.data?.next;
      const prevUrl = res?.data?.previous;
      const violationcounts = results?.count || {};
      setServices(dataList);

      const types = new Set();
      let flatRecords = [];

      const filteredDataList =
        selectedService && selectedService !== "all"
          ? dataList.filter(
              (service) => service.service_type === selectedService
            )
          : dataList;

      filteredDataList.forEach((service) => {
        service?.violations?.forEach((violation) => {
          types.add(violation.violation);
          flatRecords = flatRecords.concat(
            violation?.records?.filter((record) => {
              return selectedViolation
                ? record.violation === selectedViolation
                : true;
            })
          );
        });
      });

      const pieData = {};
      const barData = {};

      if (selectedService && selectedService !== "all") {
        const selectedServiceData = dataList.find(
          (service) => service.service_type === selectedService
        );
        if (selectedServiceData) {
          const serviceCounts =
            violationcounts[selectedServiceData.service_type] || {};
          Object.entries(serviceCounts).forEach(([violationType, count]) => {
            if (!selectedViolation || selectedViolation === violationType) {
              pieData[violationType] = count;
              barData[violationType] = count;
            }
          });
        }
      } else {
        Object.entries(violationcounts).forEach(([category, counts]) => {
          if (counts && typeof counts === "object") {
            Object.entries(counts).forEach(([violationType, count]) => {
              if (!selectedViolation || selectedViolation === violationType) {
                pieData[violationType] = (pieData[violationType] || 0) + count;
                barData[violationType] = (barData[violationType] || 0) + count;
              }
            });
          }
        });
      }

      setPieChartData({
        labels: Object.keys(pieData),
        series: Object.values(pieData),
      });

      if (selectedService === "Intrusion Detection") {
        const flatRecords = flattenData(filteredDataList);
        const { categories, series } = groupRecordsIntoIntervals(
          flatRecords,
          selectedOption,
          startDate,
          endDate
        );
        setBarChartData({
          categories,
          series: [{ name: "Violations", data: series }],
        });
        setPaginationUrls({ next: nextUrl, prev: prevUrl });
        setViolationsData(filteredDataList);
        setViolationTypes([...types]);
        return;
      }

      setBarChartData({
        categories: Object.keys(barData),
        series: [{ name: "Violations", data: Object.values(barData) }],
      });

      setPaginationUrls({ next: nextUrl, prev: prevUrl });
      setViolationsData(filteredDataList);
      setViolationTypes([...types]);
    } catch (error) {
      console.error("Fetch error:", error);
      setViolationsData([]);
      setViolationTypes([]);
      setPieChartData({ labels: [], series: [] });
      setPaginationUrls({ next: null, prev: null });
      if (error?.response?.status !== 404) {
        toast.error(
          error?.response?.data?.detail ||
            "An error occurred while fetching data"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadVideo = (videoUrl, violationTypes) => {
    if (!videoUrl) {
      setDownloadingUrl("");
      return;
    }

    fetch(videoUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `${violationTypes}-${
          selectedLocation?.location
        }-${formatDate(dataGridRows[0]?.created_at)}.mp4`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => console.error("Error in downloading the video", err))
      .finally(() => {
        setIsDownloading(false);
        setDownloadingUrl("");
      });
  };

  const fetchAndDownloadVideo = async (url, violationTypes) => {
    try {
      setIsDownloading(true);
      const key = url.replace("s3://benchmark-violation-videos/", "");
      const signedUrl = await S3SignedUrl(BUCKET, key, "video/mp4");

      const timer = setTimeout(
        () => downloadVideo(signedUrl, violationTypes),
        100
      );
      setDownloadTimer(timer);
    } catch (error) {
      setDownloadingUrl("");
      toast.error(error.response?.data?.message);
    }
  };

  const handleDownload = (url, id, violationTypes) => {
    setDownloadingUrl(url);
    setSelectedVideoUrl(id);
    clearTimeout(downloadTimer);
    fetchAndDownloadVideo(url, violationTypes);
  };

  useEffect(() => {
    setLoading(true);

    setPieChartData({ labels: [], series: [] });
    setBarChartData({
      categories: [],
      series: [{ name: "Violations", data: [] }],
    });

    const timer = setTimeout(() => {
      if (!selectedLocation?.site_id) {
        setLoading(false);
        return;
      }

      if (
        selectedOption === "custom" &&
        (!localDateRange[0] || !localDateRange[1])
      ) {
        setLoading(false);
        return;
      }

      fetchDatawithPagination();
    }, 100);

    return () => clearTimeout(timer);
  }, [
    selectedLocation?.site_id,
    selectedOption,
    selectedViolation,
    selectedDevice,
    localDateRange,
    selectedService,
  ]);

  const handelOpen = (clip) => {
    setPreviewModalOpen(true);
    setSelectedVideoUrl(clip);
  };

  const columns = [
    { field: "violation", headerName: "Violation", minWidth: 180, flex: 1 },
    { field: "description", headerName: "Description", minWidth: 400, flex: 1 },
    { field: "area", headerName: "Area", minWidth: 150, flex: 1 },
    { field: "device", headerName: "Device", minWidth: 150, flex: 1 },
    {
      field: "date",
      headerName: "Created At",
      width: 200,
      renderCell: (params) => {
        return (
          <Stack
            direction="column"
            gap={0.4}
            justifyContent="center"
            height={1}
            sx={{ color: "text.secondary" }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              {params.row.created_at?.split(" ")[0]}
            </Typography>
            <Typography variant="caption">
              {params.row.created_at?.split(" ")[1]}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "violation_image",
      headerName: "Image",
      width: 200,
      renderCell: (params) =>
        params?.row?.violation_image ? (
          <ImageCell src={params?.row?.violation_image} bucket={BUCKET} />
        ) : (
          "No Image Available"
        ),
    },
    {
      field: "video",
      headerName: "Stream Preview",
      width: 150,
      renderCell: (params) => (
        <IconButton
          sx={{ fontSize: "30px" }}
          onClick={() => handelOpen(params?.row?.violation_clip)}
        >
          <Icon sx={{ color: "info.focus" }}>preview</Icon>
        </IconButton>
      ),
    },
    {
      field: "violation_clip",
      headerName: "Download",
      width: 220,
      renderCell: (params) => (
        <>
          <Button
            variant="outlined"
            onClick={() => {
              handleDownload(params.value, params.id, params?.row?.violation);
            }}
            sx={{
              width: "140px",
              height: "38px",
              fontSize: "14px",
              fontWeight: "500",
              background: `${gradients.info.main}`,
              color: white.main,
              border: "none",
              marginRight: "5px",
              marginBottom: "0",
              "&:hover": {
                background: `${gradients.info.main} !important`,
                color: white.main,
              },
            }}
          >
            {isDownloading && selectedVideoUrl === params.id ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{ color: "white !important" }}
                />
              </>
            ) : (
              <>
                <Icon sx={{ marginRight: "5px" }}>download</Icon> Stream
              </>
            )}
          </Button>
        </>
      ),
    },
  ];

  const flattenData = (data) => {
    let flattenedRecords = [];

    data.forEach((item) => {
      item.violations.forEach((violation) => {
        violation.records.forEach((record) => {
          flattenedRecords.push({
            id: record.id,
            violation: violation.violation,
            created_at: record.created_at,
            description: record.description,
            area: record.area || "-",
            device: record.device,
            violation_clip: record.violation_clip,
            violation_image: record.violation_image,
          });
        });
      });
    });

    return flattenedRecords;
  };

  const dataGridRows = flattenData(violationsData);
  const NoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        fontSize: "20px",
        fontWeight: 500,
        color: "#666",
        opacity: "0.4",
      }}
    >
      No Data Found
    </Box>
  );

  const datePickerProps = {
    selectsRange: true,
    startDate: localDateRange[0],
    endDate: localDateRange[1],
    onChange: handleDateRangeChange,
    isClearable: false,
    dateFormat: "yyyy-MM-dd",
    placeholderText: "Select date range",
  };

  const handleServiceChange = (e) => {
    setSelectedService(e.target.value);
  };

  const formControlStyles = {
    background: "white",
    borderRadius: "10px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      fontSize: "15px",
      height: "48px",
    },
    "& .MuiInputLabel-root": {
      color: "gray",
      fontWeight: 500,
      fontSize: "14px",
    },
  };

  const customDateBtnStyles = {
    width: "245px",
    height: "45px",
    border: "none",
    background: "#063863",
    color: "white !important",
    minWidth: "150px",
    padding: "0.625rem 0.5rem",
    borderRadius: "6px",
    "&:hover": {
      background: "#063863",
      color: "white !important",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ marginTop: "22px", paddingTop: "22px" }}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            backgroundColor: "#f8f9fa",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems="center"
            gap={2}
            mt={3}
          >
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={formControlStyles}
                >
                  <InputLabel id="location-label">Select Location</InputLabel>
                  <Select
                    labelId="location-label"
                    label="Select Location"
                    value={selectedSiteId || ""}
                    onChange={handleLocationChange}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  >
                    {(siteData || []).map((site) => (
                      <MenuItem key={site?.site_id} value={site?.site_id}>
                        {site?.location}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {selectedLocation && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    sx={formControlStyles}
                  >
                    <InputLabel id="device-label">Select Device</InputLabel>
                    <Select
                      labelId="device-label"
                      label="Select Device"
                      value={selectedDevice || "all"}
                      onChange={(e) => setSelectedDevice(e.target.value)}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                      <MenuItem value="all">All Devices</MenuItem>
                      {(selectedLocation?.devices || []).map((device) => (
                        <MenuItem
                          key={device?.device_id}
                          value={device?.device_id}
                          sx={{ fontSize: "12px", fontWeight: 500 }}
                        >
                          {device?.device_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {selectedDevice && (
                      <IconButton
                        onClick={() => setSelectedDevice("")}
                        size="small"
                        sx={{ position: "absolute", right: 5, top: 6 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={formControlStyles}
                >
                  <InputLabel id="service-type-label">Service Type</InputLabel>
                  <Select
                    labelId="service-type-label"
                    label="Service Type"
                    value={selectedService || "all"}
                    onChange={handleServiceChange}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  >
                    <MenuItem value="all">All Services</MenuItem>
                    {services?.map((service, index) => (
                      <MenuItem key={index} value={service?.service_type}>
                        {service?.service_type}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedService && (
                    <IconButton
                      onClick={() => setSelectedService("")}
                      size="small"
                      sx={{ position: "absolute", right: 5, top: 6 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={formControlStyles}
                >
                  <InputLabel id="violation-type-label">
                    Violation Type
                  </InputLabel>
                  <Select
                    labelId="violation-type-label"
                    label="Violation Type"
                    value={selectedViolation || ""}
                    onChange={(e) => setSelectedViolation(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  >
                    <MenuItem value="">All Violations</MenuItem>
                    {(violationTypes || []).map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedViolation && (
                    <IconButton
                      onClick={() => setSelectedViolation("")}
                      size="small"
                      sx={{ position: "absolute", right: 5, top: 6 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={formControlStyles}
                >
                  <InputLabel id="time-range-label">Time Range</InputLabel>
                  <Select
                    labelId="time-range-label"
                    label="Time Range"
                    value={selectedOption}
                    onChange={handleSelectChange}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  >
                    <MenuItem value="24hours">Last 24 Hours</MenuItem>
                    <MenuItem value="48hours">Last 48 Hours</MenuItem>
                    <MenuItem value="72hours">Last 72 Hours</MenuItem>
                    <MenuItem value="week">Last Week</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                    <MenuItem value="all">All Time</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {selectedOption === "custom" && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <DatePicker
                    {...datePickerProps}
                    customInput={
                      <Button sx={customDateBtnStyles}>
                        <Icon
                          sx={{
                            fontSize: "18px",
                            marginBottom: "6px",
                            marginRight: "8px",
                          }}
                        >
                          calendar_month
                        </Icon>
                        Select Date Range
                      </Button>
                    }
                  />
                </Grid>
              )}
              
              <Grid xs={12} sm={6} md={4} lg={3}>
                <Box
                  display="flex"
                  justifyContent={{ xs: "center", md: "flex-start" }}
                >
                  <Button
                    sx={{
                      border: "none",
                      background: "#063863 !important",
                      color: "white !important",
                      minWidth: "250px",
                      padding: "0.625rem 0.5rem",
                      borderRadius: "6px",
                      fontWeight: 600,
                      boxShadow: "none",
                      height: "44px",
                      marginTop: "18px",
                      marginLeft: "16px",
                      "&:hover": {
                        background: "#063863 !important",
                        color: "white !important",
                      },
                    }}
                    onClick={exportToExcel}
                    startIcon={
                      isLoading ? (
                        <CircularProgress
                          size={20}
                          sx={{ color: "white !important" }}
                        />
                      ) : (
                        <Icon>download</Icon>
                      )
                    }
                  >
                    {isLoading ? "Exporting..." : "Export CSV"}
                  </Button>
                </Box>
              </Grid>

            </Grid>
          </Stack>
        </Paper>

        {violationsData?.length > 0 && (
          <Alert
            icon={<Info fontSize="inherit" />}
            severity="info"
            sx={{
              mb: 2,
              fontWeight: "500",
              fontSize: "15px",
              backgroundColor: "rgba(6, 56, 99, 0.08)",
              color: "#063863",
              "& .MuiAlert-icon": {
                color: "#063863",
              },
            }}
          >
            Click on any bar in the chart below to view detailed information
            about specific violations
          </Alert>
        )}
        <Grid
          container
          spacing={3}
          sx={{ mb: 3, display: "flex", justifyContent: "center" }}
        >
          {services[0]?.service_type !== "Intrusion Detection" && (
            <Grid
              item
              xs={12}
              md={`${
                services[0]?.service_type === "Intrusion Detection" ? 12 : 4
              }`}
            >
              <Card
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  boxShadow: 3,
                  borderRadius: 3,
                  padding: 2,
                  height: "100%",
                  width: "100%",
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      height: "380px",
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={Shimmer}
                      alt="loading.."
                      style={{ width: "100%", height: "100%", opacity: 1 }}
                    />
                  </Box>
                ) : violationsData?.length === 0 && !loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      height: "380px",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "220px",
                        height: "220px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={NoPie}
                        alt="No Data"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          opacity: 0.4,
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <ReactApexChart
                    type="pie"
                    options={{
                      ...barChartOptions,
                      labels: pieChartData?.labels,
                    }}
                    series={pieChartData?.series}
                    height="350"
                  />
                )}
              </Card>
            </Grid>
          )}

          <Grid
            item
            xs={12}
            md={`${
              services[0]?.service_type === "Intrusion Detection" ? 12 : 8
            }`}
          >
            <Card
              sx={{
                padding: 2,
                boxShadow: 3,
                borderRadius: 3,
                height: "100%",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    height: "380px",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={Shimmer}
                    alt="loading.."
                    style={{ width: "100%", opacity: 1 }}
                  />
                </Box>
              ) : violationsData?.length === 0 && !loading ? (
                <Box
                  sx={{
                    display: "flex",
                    height: "380px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "220px",
                      height: "220px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={NoBar}
                      alt="No Data"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        opacity: 0.4,
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <ReactApexChart
                  type="bar"
                  options={{
                    ...barChartOptions,
                    labels: barChartData?.categories,
                  }}
                  series={barChartData?.series}
                  height="380"
                />
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ backgroundColor: "white", py: 2 }}>
        <DataGrid
          pageSize={10}
          loading={loading}
          columns={columns}
          rows={dataGridRows}
          hideFooterPagination
          disableRowSelectionOnClick
          rowCount={dataGridRows?.length}
          rowLength={dataGridRows?.length}
          slots={{
            noRowsOverlay: NoRowsOverlay,
          }}
          pageSizeOptions={[10, 20, 30, 40, 50, 100]}
          sx={{
            boxShadow: 1,
            fontSize: "15px",
            borderRadius: "8px",

            "& .MuiDataGrid-overlayWrapperInner": { background: "white" },
            "& .MuiCircularProgress-root": { color: `${gradients.info.main}` },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#fff",
              fontWeight: "500",
            },
            "& .MuiDataGrid-cell": {
              outlineOffset: "none !important",
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader": {
              background: "#063863",
              "& .MuiSvgIcon-root": { color: "#fff !important" },
            },
            "& .MuiDataGrid-row": {
              "&:nth-of-type(odd)": { backgroundColor: "#f8f9fa" },
              "&:hover": { backgroundColor: "rgb(25 108 215 / 0.02)" },
            },
          }}
        />
        {dataGridRows?.length > 0 && (
          <Pagination
            paginationUrls={paginationUrls}
            handleNextPage={() => {
              if (paginationUrls.next) {
                fetchDatawithPagination(paginationUrls.next);
              }
            }}
            handlePrevPage={() => {
              if (paginationUrls.prev) {
                fetchDatawithPagination(paginationUrls.prev);
              }
            }}
          />
        )}
        {previewModalOpen && (
          <VideoPreviewModal
            handleOpen={previewModalOpen}
            handleClose={() => setPreviewModalOpen(false)}
            videoUrl={selectedVideoUrl}
          />
        )}
      </Box>
    </DashboardLayout>
  );
};

export default Analytics;
